/* ILS — header predictive search customization (TASK-2059)
 *
 * Patches Dawn's <predictive-search> custom element to:
 *   1. Only fire the search request from >= 3 characters (avoids noisy 1-char hits).
 *   2. Widen the searchable fields to title + product_type + variant title + SKU
 *      + vendor + tag (Dawn default omits SKU + tag). Shopify's storefront index
 *      already matches localized translations across all published languages
 *      regardless of the URL locale, so the multilingual requirement is covered
 *      by the search endpoint itself — see REPORT-2058.
 *   3. Re-rank product results client-side by prefix relevance
 *      (exact > startsWith > word-boundary > contains > other) so exact prefix
 *      matches surface at the top of the dropdown.
 *   4. TASK-6025 — make Dawn's cosmetic "Search for ..." label updater total,
 *      so it can no longer throw out of onChange() and latch the whole dropdown
 *      dead for the rest of the page view. See the long comment on the
 *      updateSearchForTerm override below and REPORT-6024 for the measurement.
 *
 * Non-goals: touching non-product result groups (queries / collections / pages).
 * Non-goals (TASK-6025): the 3-character threshold, the searchable field list,
 * the request URLs, the re-ranking, the markup, the locales. Nothing a shopper
 * searches WITH changes; only the label updater is made safe.
 */
(function () {
  'use strict';

  var MIN_CHARS = 3;
  var FIELDS = 'title,product_type,variants.title,variants.sku,vendor,tag';

  /* TASK-3332 — always aim the suggest fetch at a URL that carries the
     current market/locale prefix so it never triggers Shopify's locale
     302 (`/search/suggest` -> `/lv-lv/search/suggest`). That redirect
     re-serialized the nested `resources[options][fields]` params into a
     Ruby-hash string and made `/search/suggest` answer HTTP 422
     ("expected String to be a Hash: resources") -> empty dropdown (see
     REPORT-3321). Prefer the Liquid-rendered route (already prefixed),
     then Shopify's locale-aware root, then a bare path as last resort. */
  function suggestBase() {
    var r = window.routes && window.routes.predictive_search_url;
    if (typeof r === 'string' && r.charAt(0) === '/') return r;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'search/suggest';
  }

  /* Full query: widened fields (SKU + tag + vendor). Only safe when the
     base is already prefixed, i.e. no redirect will touch the brackets. */
  function fullUrl(base, term) {
    return base
      + '?q=' + encodeURIComponent(term)
      + '&section_id=predictive-search'
      + '&resources[options][fields]=' + encodeURIComponent(FIELDS)
      + '&resources[options][unavailable_products]=last';
  }

  /* Redirect-safe fallback: bare `q` + `section_id` (no nested bracket
     params) survive a 301/302 intact, so this still returns 200 even if
     the request is redirected. Loses only the field-widening, not the
     results themselves. */
  function minimalUrl(base, term) {
    return base
      + '?q=' + encodeURIComponent(term)
      + '&section_id=predictive-search';
  }

  /* TASK-6025 — regex-metacharacter escape. Dawn built its pattern straight
     from the shopper's query, so `mango(` raised
     "SyntaxError: Invalid regular expression: /mango(/g: Unterminated group"
     on the NEXT keystroke and froze the element exactly like the null case
     below — REPORT-6024 section 3.5, measured 5x in run p-metachar. */
  function escapeRx(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* innerText is layout-dependent — it collapses whitespace differently while
     the panel is display:none than while it is open — so the "is the label
     still in step" comparison below is made on a whitespace-normalised form.
     Only the comparison; the replacement itself runs against the raw text so
     the shopper's term is matched verbatim. */
  function normLabel(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  }

  function reRankMarkup(markup, query) {
    if (!markup || !query) return markup;
    var q = String(query).toLowerCase().trim();
    if (!q) return markup;
    try {
      var tpl = document.createElement('template');
      tpl.innerHTML = markup;
      var list = tpl.content.querySelector('#predictive-search-results-products-list');
      if (!list) return markup;
      var items = Array.prototype.slice.call(list.children);
      if (items.length < 2) return markup;
      items.forEach(function (li, idx) {
        var heading = li.querySelector('.predictive-search__item-heading');
        var title = heading ? heading.textContent.trim().toLowerCase() : '';
        var score;
        if (!title) score = 0;
        else if (title === q) score = 4;
        else if (title.indexOf(q) === 0) score = 3;
        else if ((' ' + title).indexOf(' ' + q) !== -1) score = 2;
        else if (title.indexOf(q) !== -1) score = 1;
        else score = 0;
        li.__ilsScore = score;
        li.__ilsIdx = idx;
      });
      items.sort(function (a, b) {
        if (b.__ilsScore !== a.__ilsScore) return b.__ilsScore - a.__ilsScore;
        return a.__ilsIdx - b.__ilsIdx;
      });
      items.forEach(function (li) { list.appendChild(li); });
      return tpl.innerHTML;
    } catch (e) {
      return markup;
    }
  }

  function patch() {
    var ctor = window.customElements && customElements.get && customElements.get('predictive-search');
    if (!ctor || ctor.prototype.__ilsHeaderPatched) return !!ctor;
    ctor.prototype.__ilsHeaderPatched = true;

    var origGetSearchResults = ctor.prototype.getSearchResults;
    var origRenderSearchResults = ctor.prototype.renderSearchResults;

    ctor.prototype.getSearchResults = function (searchTerm) {
      if (!searchTerm || searchTerm.length < MIN_CHARS) {
        /* TASK-3372 — `close(false)` hides the panel but keeps the stale
           `results="true"` flag (only `close(true)` clears it, and that would
           also wipe the user's 1-2 chars). Drop the flag explicitly so a
           later blur+refocus at 1-2 chars doesn't make Dawn's onFocus reopen
           an emptied dropdown (`results === 'true'` -> open()). Leaves the
           input value untouched. */
        this.close(false);
        this.removeAttribute('results');
        return;
      }
      var queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        /* TASK-6025 — hand the label bookkeeping in renderSearchResults the term
           this markup was actually fetched for (not the possibly-newer input
           value), so a stale label can be told apart from an in-step one. */
        this.__ilsRenderTerm = searchTerm;
        this.renderSearchResults(this.cachedResults[queryKey]);
        var deferredCached = this.dispatchSearchUpdateEvent(searchTerm);
        if (deferredCached && deferredCached.resolve) {
          deferredCached.resolve({ totalCount: this.getTotalResultCount() });
        }
        return;
      }

      var deferred = this.dispatchSearchUpdateEvent(searchTerm);
      var base = suggestBase();
      var self = this;

      function render(text) {
        var section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('predictive-search section missing');
        var markup = section.innerHTML;
        self.allPredictiveSearchInstances.forEach(function (inst) {
          inst.cachedResults[queryKey] = markup;
        });
        self.__ilsRenderTerm = searchTerm;   /* TASK-6025 — see the cached branch */
        self.renderSearchResults(markup);
        if (deferred && deferred.resolve) {
          deferred.resolve({ totalCount: self.getTotalResultCount() });
        }
      }

      fetch(fullUrl(base, searchTerm), { signal: this.abortController.signal })
        .then(function (response) {
          /* TASK-3372 — treat a FOLLOWED REDIRECT as a failure too, not just
             a non-200. On some market/locale prefixes Shopify answers the
             bracketed suggest URL with a 302 that it then follows to a *200*,
             but in doing so it re-serialises `resources[options][fields]` into
             a Ruby-hash string (`resources={"options"=>{...}}`) the endpoint
             can't parse -> the 200 body carries ZERO results. Because the old
             guard only retried on non-200, that redirect-to-200 slipped
             through and rendered an empty dropdown even though matching
             products exist (the exact "type a new word, get nothing" bug).
             `response.redirected` is true precisely in that case and false on
             the healthy markets, so it is the clean discriminator: only render
             the full response when it was NOT redirected. TASK-3332's 422
             branch is still covered by the same fall-through. */
          if (response.ok && !response.redirected) return response.text().then(render);
          /* Retry ONCE with the redirect-safe minimal query (bare `q` +
             `section_id`, no bracket params) before giving up, so the user
             sees results instead of a silently-closed empty dropdown. A
             redirect can't mangle a bracket-free query, so it survives 200. */
          return fetch(minimalUrl(base, searchTerm), { signal: self.abortController.signal })
            .then(function (r2) {
              if (!r2.ok) throw new Error(r2.status);
              return r2.text().then(render);
            });
        })
        .catch(function (error) {
          if (error && error.code === 20) {
            if (deferred && deferred.reject) deferred.reject(error);
            return;
          }
          if (deferred && deferred.reject) deferred.reject(error);
          self.close();
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[ils] predictive search fetch failed', error);
          }
        });
    };

    ctor.prototype.renderSearchResults = function (resultsMarkup) {
      var query = '';
      try { query = this.getQuery(); } catch (e) { query = ''; }
      var reranked = reRankMarkup(resultsMarkup, query);
      origRenderSearchResults.call(this, reranked);
      /* TASK-6025 — a render replaces the WHOLE section innerHTML, including the
         "Search for ..." button, so right here the label is authoritative and
         belongs to __ilsRenderTerm. Recording the pair is what lets
         updateSearchForTerm below distinguish a label it may safely edit in
         place from a stale one it must leave alone. The re-rank above is
         untouched and still keyed on getQuery() exactly as before. */
      try {
        var term = (typeof this.__ilsRenderTerm === 'string') ? this.__ilsRenderTerm : query;
        var lbl = this.querySelector('[data-predictive-search-search-for-text]');
        this.__ilsLabelTerm = lbl ? term : null;
        this.__ilsLabelText = lbl ? lbl.innerText : null;
        /* Dawn creates its AbortController once in the constructor and never
           replaces it, so a keystroke does NOT abort the previous suggest
           request: a slower earlier response can land AFTER a later one and
           repaint the label with an older term. Measured once per run in
           TASK-6025 acceptance (field "mande", label "Search for mand"). The
           label is server-rendered for `term`, so when the field has already
           moved on, bring it forward through the same guarded updater — which
           is in step by construction, having just been recorded above. */
        if (lbl) {
          var now = this.getQuery();
          if (typeof now === 'string' && now && now !== term) this.updateSearchForTerm(term, now);
        }
      } catch (e) {
        this.__ilsLabelTerm = null;
        this.__ilsLabelText = null;
      }
    };

    /* TASK-6025 — null-safe, regex-safe and staleness-safe replacement for Dawn
       15.5.0's updateSearchForTerm. That method only keeps the cosmetic
       "Search for ..." button label in step with the field, but onChange() calls
       it BEFORE `this.searchTerm = newSearchTerm` and BEFORE
       `getSearchResults()`, so anything it throws takes out the suggest request
       AND freezes the element's own state. Stock body:

         if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) return;

       String.prototype.match returns null when there is no match, so
       `null.length` is a TypeError. REPORT-6024 measured the consequence on the
       live store: from the second query of a page view the dropdown issued ZERO
       /search/suggest requests, threw 10-21 TypeErrors per run, kept searchTerm
       frozen at one character, and did not recover on clear, on blur/refocus or
       on reopening the panel — only a page reload cleared it.

       Why it is reachable here: the label is rendered as a SIBLING of
       #predictive-search-results-groups-wrapper (sections/predictive-search.liquid),
       so onChange's discontinuity branch, which removes only the wrapper, leaves
       the button behind holding whatever text it last had — emptied by the clear
       to `Search for ""` / `Poisk ""` / `Nach "" suchen` / `Meklet ""` /
       `Otsi ""` / `Ieskoti ""`. The MIN_CHARS gate above then renders nothing at
       the 1-character step that would have refreshed it, so the stale label
       survives into the keystroke whose regex finds nothing and throws. The gate
       is a deliberate product decision and is NOT changed; the defective line is.

       Five properties, each answering a measured failure:

         * `!previousTerm` -> no-op. Stock built `new RegExp('', 'g')`, which
           matches at every position, so `length > 1` returned early WITHOUT
           refreshing the label while onChange still advanced searchTerm. That is
           the step that armed the latch (REPORT-6024 section 3.4, step 4); an
           explicit no-op is the same outcome, now deliberately.
         * label not in step -> no-op. The label is edited only when it is still
           exactly what the last render produced, for exactly previousTerm.
           Dawn's heuristic instead counted occurrences anywhere in the label, so
           it edited stale labels and visibly corrupted them: typing `h` into an
           emptied `Search for ""` produced `Searcha for ""`, because the `h` it
           found was the one inside the word "Search" (REPORT-6024 section 2.3).
         * `!m` -> no-op instead of `null.length` — removes latch 1.
         * escaped pattern -> removes latch 2, the SyntaxError on `(`, `[`, `*`,
           `+`, `?`, `\` (REPORT-6024 section 3.5).
         * function replacement -> a `$&` or `$1` inside the shopper's own text
           is inserted literally rather than interpreted by String.replace.

       A no-op is always safe: the next render replaces the label wholesale with
       the server-rendered one, so at worst the label lags by one keystroke while
       the field is still below MIN_CHARS and the panel is closed. The whole body
       is additionally wrapped in try/catch, because the one thing this method
       must never do again is stop onChange from reaching getSearchResults(). */
    ctor.prototype.updateSearchForTerm = function (previousTerm, newTerm) {
      try {
        var el = this.querySelector('[data-predictive-search-search-for-text]');
        var text = el && el.innerText;
        if (!text || !previousTerm) return;
        if (this.__ilsLabelTerm !== previousTerm) return;
        if (normLabel(this.__ilsLabelText) !== normLabel(text)) return;
        var rx = new RegExp(escapeRx(previousTerm), 'g');
        var m = text.match(rx);
        if (!m || m.length > 1) return;
        el.innerText = text.replace(rx, function () { return newTerm; });
        this.__ilsLabelTerm = newTerm;
        this.__ilsLabelText = el.innerText;
      } catch (e) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[ils] updateSearchForTerm skipped', e);
        }
      }
    };

    // Also drop any stale cache — the old fetch (without `fields`) may already
    // have primed one entry before this script ran.
    document.querySelectorAll('predictive-search').forEach(function (el) {
      try { el.cachedResults = {}; } catch (e) {}
    });

    return true;
  }

  if (patch()) return;
  var tries = 0;
  var iv = setInterval(function () {
    tries++;
    if (patch() || tries > 200) clearInterval(iv);
  }, 30);
})();
